import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { ContractDocument, ContractConfirmingDocsError } from '../../types'

import { Contract, ContractConfirmingDoc, ContractDoc } from '../../../../types'
import LoadingIcon from '../../../../../../../components/LoadingIcon'

import ContractDocumentFileUploaderBlock from './ContractDocumentFileUploaderBlock'
import { Slide, ToastContainer } from 'react-toastify'

import HoverTooltip from '../../../../../../../components/HoverTooltip'
import ContractConfirmingDocsTableRow from './ContractConfirmingDocsTableRow'

type Props = {
    theme: string
    contractAction: boolean
    contractGuid: string | undefined
    contract: Contract
    errors?: ContractConfirmingDocsError
    contractConfirmDocs: ContractConfirmingDoc | null
    onConfirmDocs: (confirm_docs: ContractConfirmingDoc | null) => void
}

type ConfirmDocForm = {
    confirming_doc_code: string
    confirming_doc_code_title: string
    protocol_info_guid?: string
    protocol_name?: string
    doc_date: string
    doc_num: string
}

const CONFIRMING_DOC_OPTIONS = [
    {
        value: '1',
        label: 'Протокол, составленный по результатам закупки',
        disabled: false,
    },
    {
        value: '2',
        label: 'Иной документ',
        disabled: false,
    },
]

const getCurrentDate = (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0') // добавляем 1, так как месяцы начинаются с 0
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const EMPTY_FORM_1: ConfirmDocForm = {
    confirming_doc_code: '1',
    confirming_doc_code_title: 'Протокол, составленный по результатам закупки',
    protocol_info_guid: '',
    protocol_name: '',
    doc_date: getCurrentDate(),
    doc_num: '',
}

const EMPTY_FORM_2: ConfirmDocForm = {
    confirming_doc_code: '2',
    confirming_doc_code_title: 'Иной документ',
    doc_date: getCurrentDate(),
    doc_num: '',
}

const ContractConfirmingDocsTable: React.FC<Props> = (props) => {
    const [confirmDocs, setConfirmDocs] =
        useState<ContractConfirmingDoc | null>(props.contractConfirmDocs)

    const [isLoadingContractConfirmingDocs, setLoadingContractConfirmingDocs] =
        useState(false)

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [contractConfirmingDocsAddPopup, setContractConfirmingDocsAddPopup] =
        useState<boolean>(false)
    const [
        contractConfirmingDocsEditPopup,
        setContractConfirmingDocsEditPopup,
    ] = useState<boolean>(false)
    const [
        contractConfirmingDocsDeleteRowPopup,
        setContractConfirmingDocsDeleteRowPopup,
    ] = useState<boolean>(false)

    const [deleteRowIndex, setDeleteRowIndex] = useState<number | null>(null)
    const [editRowIndex, setEditRowIndex] = useState<number | null>(null)
    const [isDeleting, setDeleting] = useState<boolean>(false)

    const [form, setForm] = useState<ConfirmDocForm>(EMPTY_FORM_2)

    useEffect(() => {
        setConfirmDocs(props.contractConfirmDocs)
    }, [props.contractConfirmDocs])

    const rows = useMemo(() => {
        return confirmDocs?.contract_doc ?? []
    }, [confirmDocs])

    const emitChange = (nextRows: ContractDoc[]) => {
        const nextValue: ContractConfirmingDoc | null =
            nextRows.length > 0 ? { contract_doc: nextRows } : null

        setConfirmDocs(nextValue)
        props.onConfirmDocs(nextValue)
    }

    const resetForm = () => {
        setForm({
            confirming_doc_code: '2',
            confirming_doc_code_title: 'Иной документ',
            doc_date: props.contract.resume_date ?? getCurrentDate(),
            doc_num: '',
        })

        setEditRowIndex(null)
    }

    // popups
    const handleContractConfirmingDocsAddPopup = () => {
        resetForm()
        setContractConfirmingDocsAddPopup((prev) => !prev)
    }

    const handleContractConfirmingDocsEditPopup = (index?: number) => {
        if (index !== undefined) {
            const row = rows[index]
            if (row) {
                setForm({
                    confirming_doc_code: row.confirming_doc_code ?? '2',
                    confirming_doc_code_title:
                        row.confirming_doc_code === '1'
                            ? 'Протокол, составленный по результатам закупки'
                            : 'Иной документ',
                    protocol_info_guid:
                        row.confirming_doc_code === '1'
                            ? (row.protocol_info_guid ?? '')
                            : '',
                    protocol_name:
                        row.confirming_doc_code === '1'
                            ? (row.protocol_name ?? '')
                            : '',
                    doc_date: row.doc_date ?? '',
                    doc_num: row.doc_num ?? '',
                })
                setEditRowIndex(index)
            }
        } else {
            resetForm()
        }

        setContractConfirmingDocsEditPopup((prev) => !prev)
    }

    const handleContractConfirmingDocsDeleteRowPopup = () => {
        setContractConfirmingDocsDeleteRowPopup((prev) => !prev)
    }

    const updateForm = (field: keyof ConfirmDocForm, value: string) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }))
    }

    const handleAddClick = async () => {
        try {
            setDeleting(true)

            if (!form.doc_date) {
                return
            }

            const normalizedDocNum = form.doc_num.trim()

            const exists =
                normalizedDocNum !== '' &&
                rows.some(
                    (item) => (item.doc_num ?? '').trim() === normalizedDocNum
                )

            if (exists) {
                alert('Документ с таким номером уже существует')
                return
            }

            const newRow: ContractDoc =
                form.confirming_doc_code === '1'
                    ? {
                          confirming_doc_code: '1',
                          confirming_doc_code_title:
                              'Протокол, составленный по результатам закупки',
                          protocol_info_guid:
                              form.protocol_info_guid || undefined,
                          protocol_name: form.protocol_name || undefined,
                          doc_date: form.doc_date,
                          doc_num: form.doc_num.trim(),
                      }
                    : {
                          confirming_doc_code: '2',
                          confirming_doc_code_title: 'Иной документ',
                          doc_date: form.doc_date,
                          doc_num: form.doc_num.trim(),
                      }

            emitChange([...rows, newRow])
            setContractConfirmingDocsAddPopup(false)
            resetForm()
        } finally {
            setDeleting(false)
        }
    }

    const handleEditClick = async () => {
        try {
            setDeleting(true)

            if (editRowIndex === null) return
            if (!form.doc_date) {
                return
            }

            const normalizedDocNum = form.doc_num.trim()

            const existsAnother =
                normalizedDocNum !== '' &&
                rows.some(
                    (item, index) =>
                        index !== editRowIndex &&
                        (item.doc_num ?? '').trim() === normalizedDocNum
                )

            if (existsAnother) {
                alert('Документ с таким номером уже существует')
                return
            }

            const nextRows = rows.map((item, index) =>
                index === editRowIndex
                    ? form.confirming_doc_code === '1'
                        ? {
                              ...item,
                              confirming_doc_code: '1',
                              confirming_doc_code_title:
                                  'Протокол, составленный по результатам закупки',
                              protocol_info_guid:
                                  form.protocol_info_guid || undefined,
                              protocol_name: form.protocol_name || undefined,
                              doc_date: form.doc_date,
                              doc_num: normalizedDocNum,
                          }
                        : {
                              ...item,
                              confirming_doc_code: '2',
                              confirming_doc_code_title: 'Иной документ',
                              protocol_info_guid: undefined,
                              protocol_name: undefined,
                              doc_date: form.doc_date,
                              doc_num: normalizedDocNum,
                          }
                    : item
            )

            emitChange(nextRows)
            setContractConfirmingDocsEditPopup(false)
            resetForm()
        } finally {
            setDeleting(false)
        }
    }

    const handleDeleteClick = async () => {
        try {
            setDeleting(true)

            if (deleteRowIndex === null) return

            const nextRows = rows.filter((_, index) => index !== deleteRowIndex)
            emitChange(nextRows)

            setDeleteRowIndex(null)
            setContractConfirmingDocsDeleteRowPopup(false)
        } finally {
            setDeleting(false)
        }
    }

    const handleConfirmingDocTypeChange = (value: string) => {
        if (value === '1') {
            setForm({
                ...EMPTY_FORM_1,
                doc_date: form.doc_date,
                doc_num: form.doc_num,
            })
            return
        }

        setForm({
            ...EMPTY_FORM_2,
            doc_date: form.doc_date,
            doc_num: form.doc_num,
        })
    }

    // это потом поменять, когда появятся протоколы
    const protocolOptions: {
        value: string
        label: string
        protocol_name: string
        protocol_info_guid: string
    }[] = []

    const handleProtocolChange = (value: string) => {
        const selectedProtocol = protocolOptions.find(
            (item) => item.value === value
        )

        if (!selectedProtocol) {
            setForm((prev) => ({
                ...prev,
                protocol_name: '',
                protocol_info_guid: '',
            }))
            return
        }

        setForm((prev) => ({
            ...prev,
            protocol_name: selectedProtocol.protocol_name,
            protocol_info_guid: selectedProtocol.protocol_info_guid,
        }))
    }

    return (
        <>
            <fieldset disabled={false} className="flex w-full h-full">
                <HoverTooltip
                    tooltipText={props.errors?.contract_confirming_docs || ''}
                >
                    <div
                        className={`flex flex-col w-full bg-[#e4e6e8] dark:bg-[#242225] rounded ${
                            props.errors?.contract_confirming_docs
                                ? 'border border-[#C15C5C]'
                                : ''
                        }`}
                    >
                        {/* toolbar */}
                        <div className="flex flex-row gap-1 p-2 w-full min-h-[40px] text-[11px] rounded-t text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                            {!props.contractAction && (
                                <div className="flex w-8 h-full items-center justify-center text-center">
                                    <button
                                        onClick={
                                            handleContractConfirmingDocsAddPopup
                                        }
                                        className="w-6 h-6 grid rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    >
                                        <HoverTooltip tooltipText="Добавить">
                                            <svg
                                                className="w-4 h-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    fill={
                                                        props.theme === 'dark'
                                                            ? '#FFFFFF'
                                                            : '#181818'
                                                    }
                                                    d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                                />
                                            </svg>
                                        </HoverTooltip>
                                    </button>
                                </div>
                            )}

                            <div className="flex flex-1 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                        </div>

                        {/* header */}
                        <div className="flex flex-row gap-1 w-full h-[40px] p-[5px] items-center justify-center text-[11px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                            <div className="flex w-[40px] h-full items-center justify-center text-center">
                                №
                            </div>
                            <div className="flex w-[120px] h-full items-center justify-center text-center">
                                Номер документа
                            </div>
                            <div className="flex flex-1 h-full items-center justify-center text-center">
                                Наименование документа
                            </div>
                            <div className="flex w-[220px] h-full items-center justify-center text-center">
                                Протокол закупки
                            </div>
                            <div className="flex w-[95px] h-full items-center justify-center text-center">
                                Дата
                            </div>
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                        </div>

                        {/* rows */}
                        <div className="flex flex-col w-full gap-1 my-1">
                            {isLoadingContractConfirmingDocs ? (
                                <div className="flex w-full h-full items-center justify-center">
                                    <LoadingIcon />
                                </div>
                            ) : rows.length > 0 ? (
                                rows.map((row, index) => (
                                    <ContractConfirmingDocsTableRow
                                        key={`${row.doc_num || 'empty'}-${index}`}
                                        index={index}
                                        contractAction={props.contractAction}
                                        contractGuid={props.contractGuid}
                                        theme={props.theme}
                                        contractConfirmingDoc={row}
                                        onClickEdit={(index_row) =>
                                            handleContractConfirmingDocsEditPopup(
                                                index_row
                                            )
                                        }
                                        onDeleteRow={(index_row) => {
                                            setSelectedItems([])
                                            setDeleteRowIndex(index_row)
                                            handleContractConfirmingDocsDeleteRowPopup()
                                        }}
                                    />
                                ))
                            ) : (
                                <div className="flex w-full h-full py-1 items-center justify-center text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                    Нет данных
                                </div>
                            )}
                        </div>

                        {/* footer */}
                        <div className="flex flex-row gap-1 w-full min-h-[40px] text-[11px] p-1 rounded-b text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                            <div className="flex flex-1 gap-3 px-5 h-full items-center justify-start text-center">
                                {!props.contractAction && (
                                    <button
                                        className="flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                        onClick={
                                            handleContractConfirmingDocsAddPopup
                                        }
                                        disabled={false}
                                    >
                                        <svg
                                            className="w-3 h-3"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 22 22"
                                            id="add_supplier"
                                        >
                                            <path
                                                fill={`${props.theme == 'dark' ? '#FFFFFF' : '#181818'}`}
                                                d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                            ></path>
                                        </svg>
                                        Добавить
                                    </button>
                                )}
                            </div>
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                        </div>
                    </div>
                </HoverTooltip>
            </fieldset>

            {/* add popup */}
            {contractConfirmingDocsAddPopup && (
                <div className="fixed z-30 top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-start gap-5">
                            <div className="flex flex-col w-full items-start justify-center">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Добавить документ
                                </label>
                            </div>

                            <div className="flex flex-col w-full gap-3">
                                <div className="flex flex-row w-full gap-3">
                                    <div className="flex flex-col w-[403px]">
                                        <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Наименование документа{' '}
                                            <span className="text-[#FF0000]">
                                                *
                                            </span>
                                        </label>
                                        <select
                                            className="form-input w-full"
                                            value={form.confirming_doc_code}
                                            onChange={(e) =>
                                                handleConfirmingDocTypeChange(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            {CONFIRMING_DOC_OPTIONS.map(
                                                (option) => (
                                                    <option
                                                        key={option.value}
                                                        value={option.value}
                                                        disabled={
                                                            option.disabled
                                                        }
                                                    >
                                                        {option.label}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                </div>

                                {form.confirming_doc_code === '1' &&
                                    props.contract
                                        .notice_not_placed_by_fz223_p5_s4 ==
                                        false &&
                                    props.contract.notice_44 == false && (
                                        <div className="flex flex-row w-full gap-3">
                                            <div className="flex flex-col w-[403px]">
                                                <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                    Протокол закупки{' '}
                                                    <span className="text-[#FF0000]">
                                                        *
                                                    </span>
                                                </label>
                                                <select
                                                    className="form-input w-full"
                                                    value={
                                                        form.protocol_info_guid ??
                                                        ''
                                                    }
                                                    onChange={(e) =>
                                                        handleProtocolChange(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="">
                                                        Выберите протокол
                                                    </option>
                                                    {protocolOptions.map(
                                                        (option) => (
                                                            <option
                                                                key={
                                                                    option.value
                                                                }
                                                                value={
                                                                    option.value
                                                                }
                                                            >
                                                                {option.label}
                                                            </option>
                                                        )
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                <div className="flex flex-row w-full gap-3">
                                    <div className="flex flex-col w-[200px]">
                                        <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Дата документа{' '}
                                            <span className="text-[#FF0000]">
                                                *
                                            </span>
                                        </label>
                                        <input
                                            type="date"
                                            style={{
                                                colorScheme:
                                                    props.theme === 'dark'
                                                        ? 'dark'
                                                        : 'light',
                                            }}
                                            className="form-input w-full"
                                            value={form.doc_date}
                                            onChange={(e) =>
                                                updateForm(
                                                    'doc_date',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="flex flex-col w-[200px]">
                                        <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Номер документа{' '}
                                            <span className="text-[#FF0000]">
                                                *
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-input user-input-placeholder w-full"
                                            value={form.doc_num}
                                            onChange={(e) =>
                                                updateForm(
                                                    'doc_num',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleContractConfirmingDocsAddPopup
                                    }
                                    disabled={isDeleting}
                                >
                                    Отмена
                                </button>
                                <button
                                    className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs ${
                                        !(form.doc_date ?? null) ||
                                        !(form.doc_num ?? null) ||
                                        !(form.confirming_doc_code ?? null)
                                            ? 'text-[#5f5f5f] dark:text-[#5f5f5f] bg-[#f6f6f6] dark:bg-[#494849]'
                                            : 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                                    }`}
                                    onClick={handleAddClick}
                                    disabled={
                                        !(form.doc_date ?? null) ||
                                        !(form.doc_num ?? null) ||
                                        !(form.confirming_doc_code ?? null)
                                    }
                                >
                                    {isDeleting ? '...' : 'Добавить'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* edit popup */}
            {contractConfirmingDocsEditPopup && (
                <div className="fixed z-30 top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col w-full items-start justify-center">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Редактировать документ
                                </label>
                            </div>

                            <div className="flex flex-col w-full gap-3">
                                <div className="flex flex-row w-full gap-3">
                                    <div className="flex flex-col w-[403px]">
                                        <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Наименование документа
                                        </label>
                                        <select
                                            className="form-input w-full"
                                            value={form.confirming_doc_code}
                                            onChange={(e) =>
                                                handleConfirmingDocTypeChange(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            {CONFIRMING_DOC_OPTIONS.map(
                                                (option) => (
                                                    <option
                                                        key={option.value}
                                                        value={option.value}
                                                        disabled={
                                                            option.disabled
                                                        }
                                                    >
                                                        {option.label}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                </div>

                                {form.confirming_doc_code === '1' && (
                                    <div className="flex flex-row w-full gap-3">
                                        <div className="flex flex-col w-[403px]">
                                            <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                Протокол закупки
                                            </label>
                                            <select
                                                className="form-input w-full"
                                                value={form.protocol_info_guid}
                                                onChange={(e) =>
                                                    handleProtocolChange(
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                <option value="">
                                                    Выберите протокол
                                                </option>
                                                {protocolOptions.map(
                                                    (option) => (
                                                        <option
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.label}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                <div className="flex flex-row w-full gap-3">
                                    <div className="flex flex-col w-[200px]">
                                        <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Дата документа
                                        </label>
                                        <input
                                            type="date"
                                            style={{
                                                colorScheme:
                                                    props.theme === 'dark'
                                                        ? 'dark'
                                                        : 'light',
                                            }}
                                            className="form-input w-full"
                                            value={form.doc_date}
                                            onChange={(e) =>
                                                updateForm(
                                                    'doc_date',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="flex flex-col w-[200px]">
                                        <label className="w-full mb-1 text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Номер документа
                                        </label>
                                        <input
                                            type="text"
                                            className="form-input user-input-placeholder w-full"
                                            value={form.doc_num}
                                            onChange={(e) =>
                                                updateForm(
                                                    'doc_num',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={() =>
                                        handleContractConfirmingDocsEditPopup()
                                    }
                                    disabled={isDeleting}
                                >
                                    Отмена
                                </button>
                                <button
                                    className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs ${
                                        !(form.doc_date ?? null) ||
                                        !(form.doc_num ?? null) ||
                                        !(form.confirming_doc_code ?? null)
                                            ? 'text-[#5f5f5f] dark:text-[#5f5f5f] bg-[#f6f6f6] dark:bg-[#494849]'
                                            : 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                                    }`}
                                    onClick={handleEditClick}
                                    disabled={
                                        !(form.doc_date ?? null) ||
                                        !(form.doc_num ?? null) ||
                                        !(form.confirming_doc_code ?? null)
                                    }
                                >
                                    {isDeleting ? '...' : 'Сохранить'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* delete popup */}
            {contractConfirmingDocsDeleteRowPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center z-50">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить документ?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleContractConfirmingDocsDeleteRowPopup
                                    }
                                    disabled={isDeleting}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={handleDeleteClick}
                                >
                                    {isDeleting ? '...' : 'Удалить'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ContractConfirmingDocsTable
